import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useSearchParams } from 'react-router-dom';
import { Modal, message, Select, Input, Switch, DatePicker, Spin, Tooltip, Badge, Radio } from 'antd';
import dayjs from 'dayjs';
import {
  fetchDispatchBatches,
  fetchDispatchStages,
  fetchDispatchSteps,
  fetchBatchChecklist,
  createDispatchBatch,
  tickDispatchStep,
  untickDispatchStep,
  triggerAutoTick,
  toggleConditionalStage,
  fetchPendingGrid,
  fetchProductDrugCodes,
  fetchProductMfgStages,
  fetchTeamKanban,
  fetchDispatchTimeline,
  fetchDispatchPlanner,
  assignDispatchWeeks
} from './api';

const { Search } = Input;
const { RangePicker } = DatePicker;

// ── Week helper (weeks start Friday, period 26th prev → 25th curr) ──
const getBusinessWeeksInMonth = (monthDate) => {
  if (!monthDate) return [];
  const month = dayjs(monthDate);
  const periodStart = month.subtract(1, 'month').date(26).startOf('day');
  const periodEnd = month.date(25).endOf('day');
  const weeks = [];
  let weekNumber = 1;
  let currentFriday = periodStart.day(5);
  if (currentFriday.isAfter(periodStart)) currentFriday = currentFriday.subtract(7, 'day');
  const countDaysInPeriod = (ws, we) => {
    let count = 0; let day = ws;
    while (day.isBefore(we) || day.isSame(we, 'day')) {
      if ((day.isAfter(periodStart) || day.isSame(periodStart, 'day')) &&
          (day.isBefore(periodEnd)  || day.isSame(periodEnd,  'day'))) count++;
      day = day.add(1, 'day');
    }
    return count;
  };
  while (currentFriday.isBefore(periodEnd.add(7, 'day'))) {
    const ws = currentFriday.startOf('day');
    const we = currentFriday.add(6, 'day').endOf('day');
    if (countDaysInPeriod(ws, we.startOf('day')) >= 4) {
      weeks.push({ weekNumber, label: `Week ${weekNumber} (${ws.format('DD MMM')} - ${we.format('DD MMM')})`, startDate: ws, endDate: we });
      weekNumber++;
    }
    currentFriday = currentFriday.add(7, 'day');
  }
  return weeks;
};

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════
const TEAM_COLORS = {
  'Automated': '#3b82f6',
  'Production': '#d97706',
  'QA': '#8b5cf6',
  'QA Dispatch': '#7c3aed',
  'QC': '#10b981',
  'Engineering': '#ef4444',
  'Validation': '#6366f1',
  'MQA': '#8b5cf6',
  'AQA': '#a855f7',
  'TT': '#f59e0b',
};

const COND_LABELS = { dev: 'Deviation/Investigation', tcc: 'TC/CC', val: 'Validation/Verification' };
const COND_COLORS = {
  dev: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', headerBg: '#7f1d1d' },
  tcc: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d', headerBg: '#78350f' },
  val: { bg: '#ede9fe', color: '#5b21b6', border: '#c4b5fd', headerBg: '#4c1d95' },
};

const getTeamColor = (team) => TEAM_COLORS[team] || '#9ca3af';

const formatDate = (iso) => {
  if (!iso) return '--';
  return dayjs(iso).format('DD MMM YY, hh:mm A');
};

const formatElapsed = (from, to) => {
  if (!from) return null;
  const diff = dayjs(to || new Date()).diff(dayjs(from));
  if (diff < 0) return null;
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hours > 0 ? `+${hours}h ${mins}m` : `+${mins}m`;
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const OsdDispatch = () => {
  const [searchParams] = useSearchParams();

  // Tab state
  const [activeTab, setActiveTab] = useState(1);

  // Master data
  const [stages, setStages] = useState([]);
  const [steps, setSteps] = useState([]);
  const [products, setProducts] = useState([]);

  // Batch list
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [showInProgress, setShowInProgress] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);

  // Date filters — default 1M
  const [filterMode, setFilterMode] = useState('date');
  const [dateRange, setDateRange] = useState([dayjs().subtract(1, 'month').startOf('day'), dayjs()]);
  const [activeQuickRange, setActiveQuickRange] = useState('1M');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const availableWeeks = useMemo(() => getBusinessWeeksInMonth(selectedMonth), [selectedMonth]);

  // Selected batch checklist
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);

  // Pending grid (Tab 2)
  const [pendingGrid, setPendingGrid] = useState(null);
  const [pendingGridLoading, setPendingGridLoading] = useState(false);
  const [gridBatchFilter, setGridBatchFilter] = useState('');
  const [gridTeamFilter, setGridTeamFilter] = useState('');

  // Team kanban (Tab 3)
  const [teamKanban, setTeamKanban] = useState([]);
  const [teamKanbanLoading, setTeamKanbanLoading] = useState(false);

  // Timeline (Tab 4)
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Dispatch Planner (Tab 5)
  const [plannerData, setPlannerData] = useState(null); // { weeks, batches }
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]); // multi-select in sidebar
  const [plannerMonth, setPlannerMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });

  // Create batch modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newBatchNo, setNewBatchNo] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Conditional modal
  const [condModalOpen, setCondModalOpen] = useState(false);
  const [condModalData, setCondModalData] = useState(null);
  const [condMfgStages, setCondMfgStages] = useState([]);

  // ─── Load master data on mount ───
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [stagesData, stepsData, productsData] = await Promise.all([
          fetchDispatchStages(),
          fetchDispatchSteps(),
          fetchProductDrugCodes()
        ]);
        setStages(stagesData);
        setSteps(stepsData);
        setProducts(productsData);
      } catch (err) {
        message.error('Failed to load master data');
      }
    };
    loadMasters();
  }, []);

  // ─── Load batches on mount and when filters change ───
  const [initialSyncDone, setInitialSyncDone] = useState(true); // cron handles sync
  const loadBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const data = await fetchDispatchBatches({
        product:    productFilter || undefined,
        date_from:  dateRange?.[0]?.toISOString(),
        date_to:    dateRange?.[1]?.toISOString(),
        filter_field: activeTab !== 1 ? 'dispatch_week' : undefined,
      });
      setBatches(data);
    } catch (err) {
      message.error('Failed to load batches');
    } finally {
      setBatchesLoading(false);
    }
  }, [productFilter, dateRange, activeTab]);

  // Reload when filters change (after initial sync)
  useEffect(() => {
    if (initialSyncDone) loadBatches();
  }, [productFilter, dateRange, initialSyncDone, loadBatches]);

  // ─── Load checklist for selected batch ───
  const loadChecklist = useCallback(async (batchId) => {
    if (!batchId) return;
    setChecklistLoading(true);
    try {
      const data = await fetchBatchChecklist(batchId);
      setChecklist(data);
    } catch (err) {
      message.error('Failed to load checklist');
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeBatchId) loadChecklist(activeBatchId);
  }, [activeBatchId, loadChecklist]);

  // ─── Auto-select batch from URL param (?batchId=123) ───
  useEffect(() => {
    const batchId = searchParams.get('batchId');
    if (batchId) {
      setActiveTab(2);
      setActiveBatchId(Number(batchId));
    }
  }, [searchParams]);

  // ─── Load pending grid ───
  const loadPendingGrid = useCallback(async () => {
    setPendingGridLoading(true);
    try {
      const data = await fetchPendingGrid({
        product:     productFilter  || undefined,
        date_from:   dateRange?.[0]?.toISOString(),
        date_to:     dateRange?.[1]?.toISOString(),
        in_progress: showInProgress ? '1' : '0',
        completed:   showCompleted  ? '1' : '0',
        filter_field: 'dispatch_week',
      });
      setPendingGrid(data);
    } catch (err) {
      message.error('Failed to load pending grid');
    } finally {
      setPendingGridLoading(false);
    }
  }, [productFilter, dateRange, showInProgress, showCompleted]);

  useEffect(() => {
    if (activeTab === 3) loadPendingGrid();
  }, [activeTab, loadPendingGrid]);

  // ─── Load team kanban ───
  const loadTeamKanban = useCallback(async () => {
    setTeamKanbanLoading(true);
    try {
      const data = await fetchTeamKanban({
        date_from:   dateRange?.[0]?.toISOString(),
        date_to:     dateRange?.[1]?.toISOString(),
        in_progress: showInProgress ? '1' : '0',
        completed:   showCompleted  ? '1' : '0',
        product:     productFilter  || undefined,
        filter_field: 'dispatch_week',
      });
      setTeamKanban(data);
    } catch (err) {
      message.error('Failed to load team kanban');
    } finally {
      setTeamKanbanLoading(false);
    }
  }, [dateRange, showInProgress, showCompleted, productFilter]);

  useEffect(() => {
    if (activeTab === 4) loadTeamKanban();
  }, [activeTab, loadTeamKanban]);

  const loadTimeline = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const data = await fetchDispatchTimeline({
        date_from:   dateRange?.[0]?.toISOString(),
        date_to:     dateRange?.[1]?.toISOString(),
        in_progress: showInProgress ? '1' : '0',
        completed:   showCompleted  ? '1' : '0',
        product:     productFilter  || undefined,
        filter_field: 'dispatch_week',
      });
      setTimeline(data);
    } catch (err) {
      message.error('Failed to load timeline');
    } finally {
      setTimelineLoading(false);
    }
  }, [dateRange, showInProgress, showCompleted, productFilter]);

  useEffect(() => {
    if (activeTab === 5) loadTimeline();
  }, [activeTab, loadTimeline]);

  // ─── Load dispatch planner ───
  const loadPlanner = useCallback(async () => {
    setPlannerLoading(true);
    try {
      const data = await fetchDispatchPlanner({
        months: 4,
        from: plannerMonth,
        date_from: dateRange?.[0]?.toISOString(),
        date_to:   dateRange?.[1]?.toISOString(),
      });
      setPlannerData(data);
    } catch (err) {
      message.error('Failed to load planner');
    } finally {
      setPlannerLoading(false);
    }
  }, [plannerMonth, dateRange]);

  useEffect(() => {
    if (activeTab === 1) loadPlanner();
  }, [activeTab, loadPlanner]);

  const handlePlannerAssign = useCallback(async (updates) => {
    try {
      await assignDispatchWeeks(updates);
      // Optimistically update plannerData batches
      setPlannerData(prev => {
        if (!prev) return prev;
        const updMap = {};
        updates.forEach(u => { updMap[u.id] = u; });
        return {
          ...prev,
          batches: prev.batches.map(b =>
            updMap[b.id]
              ? { ...b, dispatch_week_start: updMap[b.id].dispatch_week_start, dispatch_week_order: updMap[b.id].dispatch_week_order }
              : b
          )
        };
      });
      // Also refresh the batch list so week badge appears
      loadBatches();
    } catch (err) {
      message.error('Failed to save assignment');
    }
  }, [loadBatches]);

  // ─── Filtered batches ───
  const filteredBatches = useMemo(() => {
    return batches.filter(b => {
      if (searchText) {
        const q = searchText.toLowerCase();
        const parentNo = (b.parent_blend_batch_no || b.blend_batch_no).toLowerCase();
        const batchNo = b.blend_batch_no.toLowerCase();
        const product = b.product_name?.toLowerCase() || '';
        if (!parentNo.includes(q) && !batchNo.includes(q) && !product.includes(q)) return false;
      }
      const isDone = b.progressPct === 100;
      if (!showInProgress && !isDone) return false;
      if (!showCompleted && isDone) return false;
      return true;
    });
  }, [batches, searchText, showInProgress, showCompleted]);

  const activeBatchCount = useMemo(() => filteredBatches.filter(b => b.progressPct < 100).length, [filteredBatches]);
  const completedBatchCount = useMemo(() => filteredBatches.filter(b => b.progressPct === 100).length, [filteredBatches]);

  // All unique teams from steps
  const allTeams = useMemo(() => [...new Set(steps.map(s => s.team))].sort(), [steps]);

  // ─── Quick range handler ───
  const handleQuickRange = (range) => {
    setActiveQuickRange(range);
    const now = dayjs();
    const map = { '1D': [now.subtract(1,'day'), now], '1W': [now.subtract(1,'week'), now], '1M': [now.subtract(1,'month').startOf('day'), now], '3M': [now.subtract(3,'month').startOf('day'), now], '6M': [now.subtract(6,'month').startOf('day'), now] };
    if (map[range]) setDateRange(map[range]);
  };

  // ─── Week select handler ───
  const handleWeekSelect = (weekValue) => {
    setSelectedWeek(weekValue);
    if (!selectedMonth) return;
    if (weekValue === 'all') {
      const month = dayjs(selectedMonth);
      setDateRange([month.subtract(1,'month').date(26).startOf('day'), month.date(25).endOf('day')]);
    } else {
      const week = availableWeeks.find(w => w.weekNumber === weekValue);
      if (week) setDateRange([week.startDate, week.endDate]);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setSelectedWeek('all');
    if (month) {
      const m = dayjs(month);
      setDateRange([m.subtract(1,'month').date(26).startOf('day'), m.date(25).endOf('day')]);
    }
  };

  // ─── Actions ───
  const handleSelectBatch = (batchId) => {
    setActiveBatchId(batchId);
  };

  const handleTickStep = async (stepId) => {
    if (!activeBatchId) return;
    try {
      await tickDispatchStep(activeBatchId, stepId, 'User');
      await loadChecklist(activeBatchId);
      loadBatches();
      message.success('Step ticked');
    } catch (err) {
      message.error(err.message || 'Failed to tick step');
    }
  };

  const handleUntickStep = async (stepId, untickedBy, untickReason) => {
    if (!activeBatchId) return;
    try {
      await untickDispatchStep(activeBatchId, stepId, untickedBy, untickReason);
      await loadChecklist(activeBatchId);
      loadBatches();
      message.success('Step unticked');
    } catch (err) {
      message.error(err.message || 'Failed to untick step');
    }
  };

  const handleAutoTick = async () => {
    if (!activeBatchId) return;
    try {
      const result = await triggerAutoTick(activeBatchId);
      if (result.data && result.data.length > 0) {
        message.success(`Auto-ticked ${result.data.length} step(s)`);
      } else {
        message.info('No steps to auto-tick');
      }
      await loadChecklist(activeBatchId);
      loadBatches();
    } catch (err) {
      message.error(err.message || 'Auto-tick failed');
    }
  };

  const handleToggleConditional = (condKey) => {
    if (!activeBatchId || !checklist) return;
    const tracker = checklist.tracker;
    const isOn = condKey === 'dev' ? tracker.has_deviation : condKey === 'tcc' ? tracker.has_tcc : tracker.has_validation;

    if (isOn) {
      // Turning off — ask for name and reason
      setCondMfgStages([]);
      setCondModalData({ condKey, enable: false, stageName: null, removedBy: '', removeReason: '' });
      setCondModalOpen(true);
    } else {
      // Turning on — ask which stage; fetch applicable mfg stages for dropdown
      setCondMfgStages([]);
      setCondModalData({ condKey, enable: true, stageName: '' });
      setCondModalOpen(true);
      const productId = checklist?.tracker?.product_drug_code_id;
      if (productId) {
        fetchProductMfgStages(productId)
          .then(data => setCondMfgStages(data.filter(s => s.is_applicable)))
          .catch(() => {});
      }
    }
  };

  const handleConfirmConditional = async () => {
    if (!activeBatchId || !condModalData) return;
    // When disabling, validate name and reason
    if (!condModalData.enable) {
      if (!condModalData.removedBy?.trim()) {
        message.warning('Please enter who is removing this conditional');
        return;
      }
      if (!condModalData.removeReason?.trim()) {
        message.warning('Please enter a reason for removing this conditional');
        return;
      }
    }
    try {
      await toggleConditionalStage(
        activeBatchId,
        condModalData.condKey,
        condModalData.enable,
        condModalData.stageName || null,
        condModalData.enable ? null : condModalData.removedBy?.trim(),
        condModalData.enable ? null : condModalData.removeReason?.trim()
      );
      setCondModalOpen(false);
      setCondModalData(null);
      await loadChecklist(activeBatchId);
      loadBatches();
      message.success(condModalData.enable ? 'Conditional stage enabled' : 'Conditional stage removed');
    } catch (err) {
      message.error(err.message || 'Failed to toggle conditional stage');
    }
  };

  const handleCreateBatch = async () => {
    if (!newBatchNo.trim()) {
      message.warning('Please enter a batch number');
      return;
    }
    setCreateLoading(true);
    try {
      const created = await createDispatchBatch(newBatchNo.trim(), 'User');
      message.success(`Dispatch tracker created for ${created.blend_batch_no}`);
      setCreateModalOpen(false);
      setNewBatchNo('');
      loadBatches();
      setActiveBatchId(created.id);
    } catch (err) {
      message.error(err.message || 'Failed to create batch');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleJumpToBatch = (batchId) => {
    setActiveTab(2);
    setActiveBatchId(batchId);
  };

  // ═══════════════════════════════════════════════════════════
  // EXCEL EXPORTS
  // ═══════════════════════════════════════════════════════════
  const exportPlanner = useCallback(() => {
    if (!plannerData) return;
    const today = new Date().toISOString().slice(0, 10);
    const { batches: pBatches } = plannerData;

    // Build month/week structure same as the board
    const [y, m] = plannerMonth.split('-').map(Number);
    const monthList = [];
    for (let i = 0; i < 4; i++) {
      let ny = y, nm = m + i;
      while (nm > 12) { nm -= 12; ny++; }
      const key = `${ny}-${String(nm).padStart(2, '0')}`;
      const label = new Date(ny, nm - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
      monthList.push({ key, label, weeks: getWeeksForMonth(key) });
    }

    // Build week → batches map (sorted by order)
    const wMap = {};
    pBatches
      .filter(b => b.dispatch_week_start)
      .sort((a, b) => a.dispatch_week_order - b.dispatch_week_order)
      .forEach(b => {
        const k = (b.dispatch_week_start || '').split('T')[0];
        if (!wMap[k]) wMap[k] = [];
        wMap[k].push(b);
      });

    const rows = [];

    // Header
    rows.push(['Month', 'Week', 'Week Period', 'Order', 'Batch No', 'Product', 'Progress %', 'Status', 'Start Date']);

    monthList.forEach(mo => {
      mo.weeks.forEach(wk => {
        const cards = wMap[wk.week_start] || [];
        if (cards.length === 0) {
          rows.push([mo.label, `Week ${wk.week_num}`, fmtWeekDates(wk.week_start, wk.week_end), '', '—', '—', '', '', '']);
        } else {
          cards.forEach((b, idx) => {
            rows.push([
              idx === 0 ? mo.label : '',
              idx === 0 ? `Week ${wk.week_num}` : '',
              idx === 0 ? fmtWeekDates(wk.week_start, wk.week_end) : '',
              idx + 1,
              b.parent_blend_batch_no || b.blend_batch_no,
              b.product_name || '',
              b.progress_pct ?? 0,
              b.status || '',
              b.start_date ? b.start_date.split('T')[0] : '',
            ]);
          });
        }
      });
    });

    // Unassigned batches section
    const unassigned = pBatches.filter(b => !b.dispatch_week_start);
    if (unassigned.length > 0) {
      rows.push([]);
      rows.push(['Unassigned Batches', '', '', '', '', '', '', '', '']);
      unassigned.forEach(b => {
        rows.push([
          '', '', '', '',
          b.parent_blend_batch_no || b.blend_batch_no,
          b.product_name || '',
          b.progress_pct ?? 0,
          b.status || '',
          b.start_date ? b.start_date.split('T')[0] : '',
        ]);
      });
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 20 }, { wch: 7 }, { wch: 18 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dispatch Planner');
    XLSX.writeFile(wb, `OSD_DispatchPlanner_${today}.xlsx`);
  }, [plannerData, plannerMonth]);

  const exportPendingDocuments = useCallback(() => {
    if (!pendingGrid) return;
    const { grid, steps: allSteps, stages: allStages } = pendingGrid;
    const today = new Date().toISOString().slice(0, 10);

    // Build stage order map
    const stageStepMap = {};
    allStages.forEach(s => { stageStepMap[s.id] = []; });
    allSteps.forEach(s => { if (stageStepMap[s.dispatch_stage_id]) stageStepMap[s.dispatch_stage_id].push(s); });

    // Header row 1: Batch No | Product | [Stage merged] | Total Pending
    const hrow1 = ['Batch No', 'Product'];
    allStages.forEach(stage => {
      const stageSteps = stageStepMap[stage.id] || [];
      stageSteps.forEach((_, i) => hrow1.push(i === 0 ? stage.stage_name : ''));
    });
    hrow1.push('Total Pending');

    // Header row 2: step names
    const hrow2 = ['', ''];
    allSteps.forEach(s => hrow2.push(s.step_name));
    hrow2.push('');

    const rows = [hrow1, hrow2];
    grid.forEach(row => {
      const pendingCount = row.stepStates.filter(ss => ss.state === 'pending').length;
      const dataRow = [row.tracker.parent_blend_batch_no || row.tracker.blend_batch_no, row.tracker.product_name];
      row.stepStates.forEach(ss => dataRow.push(ss.state === 'pending' ? 'Pending' : ''));
      dataRow.push(pendingCount);
      rows.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Merge stage header cells
    ws['!merges'] = [];
    let col = 2;
    allStages.forEach(stage => {
      const cnt = (stageStepMap[stage.id] || []).length;
      if (cnt > 1) ws['!merges'].push({ s: { r: 0, c: col }, e: { r: 0, c: col + cnt - 1 } });
      col += cnt;
    });
    ws['!cols'] = [{ wch: 18 }, { wch: 28 }, ...allSteps.map(() => ({ wch: 12 })), { wch: 14 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pending Documents');
    XLSX.writeFile(wb, `OSD_PendingDocs_${today}.xlsx`);
  }, [pendingGrid]);

  const exportTeamView = useCallback(() => {
    if (!teamKanban || teamKanban.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);

    const rows = [['Department', 'Batch No', 'Product', 'Stage', 'Step Name', 'Status', 'Ageing']];
    teamKanban.forEach(({ team, steps }) => {
      steps.forEach(step => {
        let ageing = '—';
        if (step.actionable_since) {
          const diffMs = Date.now() - new Date(step.actionable_since).getTime();
          const h = Math.floor(diffMs / 3600000);
          const m = Math.floor((diffMs % 3600000) / 60000);
          ageing = h > 0 ? `${h}h ${m}m` : `${m}m`;
        }
        rows.push([
          team,
          step.parent_blend_batch_no || step.blend_batch_no,
          step.product_name,
          step.stage_name,
          step.step_name,
          step.is_actionable ? 'Pending' : 'Coming',
          ageing,
        ]);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 28 }, { wch: 20 }, { wch: 28 }, { wch: 10 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dept Pending');
    XLSX.writeFile(wb, `OSD_DeptPending_${today}.xlsx`);
  }, [teamKanban]);

  const exportTimeline = useCallback(() => {
    if (!timeline || timeline.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);

    // Build ordered column list
    const colMap = new Map();
    for (const batch of timeline) {
      for (const stage of batch.stages) {
        for (const step of stage.steps) {
          if (!colMap.has(step.id)) {
            colMap.set(step.id, { step_id: step.id, step_name: step.step_name, team: step.team, stage_name: stage.stage_name, stage_order: stage.stage_order, step_order: step.step_order });
          }
        }
      }
    }
    const columns = Array.from(colMap.values()).sort((a, b) =>
      a.stage_order !== b.stage_order ? a.stage_order - b.stage_order : a.step_order - b.step_order
    );

    // 3 header rows
    const hrow1 = ['Batch No', 'Product'];
    const hrow2 = ['', ''];
    const hrow3 = ['', ''];
    const stageGroups = [];
    for (const col of columns) {
      const last = stageGroups[stageGroups.length - 1];
      if (last && last.stage_name === col.stage_name) { last.count++; } else { stageGroups.push({ stage_name: col.stage_name, count: 1 }); }
    }
    stageGroups.forEach(sg => {
      hrow1.push(sg.stage_name);
      for (let i = 1; i < sg.count; i++) hrow1.push('');
    });
    columns.forEach(col => { hrow2.push(col.step_name); hrow3.push(col.team || ''); });

    const rows = [hrow1, hrow2, hrow3];

    // Merges for stage row
    const merges = [];
    let c = 2;
    stageGroups.forEach(sg => {
      if (sg.count > 1) merges.push({ s: { r: 0, c }, e: { r: 0, c: c + sg.count - 1 } });
      c += sg.count;
    });

    // Data rows
    timeline.forEach(batch => {
      const stepMap = {};
      batch.stages.forEach(stage => stage.steps.forEach(step => { stepMap[step.id] = step; }));
      const row = [batch.parent_blend_batch_no || batch.blend_batch_no, batch.product_name];
      columns.forEach(col => {
        const step = stepMap[col.step_id];
        if (!step) { row.push('N/A'); return; }
        if (step.wait_days == null) { row.push(step.is_waiting ? 'Waiting' : ''); return; }
        row.push(parseFloat((step.wait_days * 24).toFixed(2)));
      });
      rows.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!merges'] = merges;
    ws['!cols'] = [{ wch: 18 }, { wch: 28 }, ...columns.map(() => ({ wch: 10 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TAT Analysis');
    XLSX.writeFile(wb, `OSD_TAT_${today}.xlsx`);
  }, [timeline]);

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div style={{ height: 'calc(100vh - 53px)', display: 'flex', flexDirection: 'column', background: '#f0f2f5', overflow: 'hidden' }}>

      {/* ── HEADER ── */}
      <div style={{ background: '#1e3a5f', color: '#fff', padding: '0 18px', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, boxShadow: '0 2px 10px rgba(0,0,0,.25)', zIndex: 500 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Dispatch Tracker</span>
          <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', background: 'rgba(255,255,255,.14)', padding: '2px 7px', borderRadius: 3 }}>Batch Management</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)' }}><b style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff', fontWeight: 700 }}>{activeBatchCount}</b> Active</div>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,.2)' }} />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)' }}><b style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff', fontWeight: 700 }}>{completedBatchCount}</b> Dispatched</div>
        </div>
      </div>

      {/* ── TAB BAR ── */}
      <div style={{ background: '#243f6a', display: 'flex', padding: '0 18px', flexShrink: 0 }}>
        {[{ id: 1, label: 'Dispatch Planner' }, { id: 2, label: 'Batch Checklist' }, { id: 3, label: 'Pending Documents' }, { id: 4, label: 'Team View' }, { id: 5, label: 'TAT Analysis' }].map(tab => (
          <div key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '8px 16px 7px', fontSize: 11, fontWeight: 600,
            color: activeTab === tab.id ? '#fff' : 'rgba(255,255,255,.5)',
            cursor: 'pointer', borderBottom: `2px solid ${activeTab === tab.id ? '#60a5fa' : 'transparent'}`,
            transition: 'all .14s', userSelect: 'none'
          }}>
            {tab.label}
          </div>
        ))}
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #d1d5db', padding: '5px 18px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>

        {/* Filter By toggle */}
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .4, color: '#9ca3af', whiteSpace: 'nowrap' }}>Filter By:</span>
        <Radio.Group size="small" value={filterMode} buttonStyle="solid" onChange={e => {
          const mode = e.target.value;
          setFilterMode(mode);
          if (mode === 'date') {
            setSelectedWeek(null); setSelectedMonth(null);
            handleQuickRange('1M');
          } else {
            const now = dayjs();
            setSelectedMonth(now);
            const weeks = getBusinessWeeksInMonth(now);
            const cur = weeks.find(w => !now.isBefore(w.startDate,'day') && !now.isAfter(w.endDate,'day'));
            const pick = cur || weeks[0];
            if (pick) { setSelectedWeek(pick.weekNumber); setDateRange([pick.startDate, pick.endDate]); }
            else { setSelectedWeek('all'); }
          }
        }}>
          <Radio.Button value="date" style={{ fontSize: 10 }}>Date Range</Radio.Button>
          <Radio.Button value="week" style={{ fontSize: 10 }}>Week</Radio.Button>
        </Radio.Group>

        <div style={{ width: 1, height: 18, background: '#d1d5db', flexShrink: 0 }} />

        {/* Date Range mode */}
        {filterMode === 'date' && (<>
          <RangePicker
            size="small"
            format="DD-MMM-YY"
            value={dateRange}
            onChange={dates => { setDateRange(dates); setActiveQuickRange(null); }}
            allowClear={false}
            presets={[
              { label: 'Last 7 Days',  value: [dayjs().subtract(7,'day').startOf('day'), dayjs()] },
              { label: 'Last 30 Days', value: [dayjs().subtract(30,'day').startOf('day'), dayjs()] },
              { label: 'This Month',   value: [dayjs().startOf('month'), dayjs()] },
            ]}
            style={{ fontSize: 10 }}
          />
          <div style={{ display: 'flex', gap: 3, padding: '2px', background: '#f7fafc', borderRadius: 5, border: '1px solid #e2e8f0' }}>
            {['1D','1W','1M','3M','6M'].map(r => (
              <button key={r} onClick={() => handleQuickRange(r)} style={{
                padding: '2px 8px', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 9, fontWeight: 700,
                background: activeQuickRange === r ? '#2c5282' : 'transparent',
                color: activeQuickRange === r ? '#fff' : '#4b5563',
              }}>{r}</button>
            ))}
          </div>
        </>)}

        {/* Week mode */}
        {filterMode === 'week' && (<>
          <DatePicker size="small" picker="month" value={selectedMonth} onChange={handleMonthChange} format="MMM YYYY" placeholder="Select month" style={{ width: 130, fontSize: 10 }} />
          <Select size="small" value={selectedWeek} onChange={handleWeekSelect} disabled={!selectedMonth} style={{ width: 230, fontSize: 10 }} placeholder={!selectedMonth ? 'Select month first' : 'Select week'}
            options={[{ value: 'all', label: 'All Weeks (Full Month)' }, ...availableWeeks.map(w => ({ value: w.weekNumber, label: w.label }))]}
          />
          <span style={{ fontSize: 9, color: '#6b7280', fontStyle: 'italic' }}>(Weeks start Friday)</span>
        </>)}

        <div style={{ width: 1, height: 18, background: '#d1d5db', flexShrink: 0 }} />

        {/* Product filter */}
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .4, color: '#9ca3af', whiteSpace: 'nowrap' }}>Product</span>
        <Select
          size="small" allowClear placeholder="All Products" value={productFilter || undefined}
          onChange={v => setProductFilter(v || '')}
          style={{ width: 160, fontSize: 10 }}
          options={[...new Set(batches.map(b => b.product_name))].filter(Boolean).sort().map(p => ({ label: p, value: p }))}
        />

        <div style={{ width: 1, height: 18, background: '#d1d5db', flexShrink: 0 }} />

        {/* Status toggles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Switch size="small" checked={showInProgress} onChange={setShowInProgress} />
          <span style={{ fontSize: 10, fontWeight: 600, color: '#4b5563' }}>In Progress</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Switch size="small" checked={showCompleted} onChange={setShowCompleted} />
          <span style={{ fontSize: 10, fontWeight: 600, color: '#4b5563' }}>Completed</span>
        </div>

        <div style={{ width: 1, height: 18, background: '#d1d5db', flexShrink: 0 }} />

        {/* Global batch search */}
        <Search
          size="small"
          placeholder="Search batch no / product..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
          style={{ width: 200, fontSize: 10 }}
        />

        {/* Clear Filters */}
        <button
          onClick={() => {
            setFilterMode('date');
            setDateRange([dayjs().subtract(1, 'month').startOf('day'), dayjs()]);
            setActiveQuickRange('1M');
            setSelectedMonth(null);
            setSelectedWeek(null);
            setProductFilter('');
            setShowInProgress(true);
            setShowCompleted(false);
            setSearchText('');
          }}
          style={{ padding: '3px 10px', fontSize: 9, fontWeight: 700, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          ✕ Clear Filters
        </button>


      </div>

      {/* ── TAB CONTENT ── */}
      {activeTab === 1 && (
        <DispatchPlannerTab
          plannerData={plannerData}
          loading={plannerLoading}
          batches={filteredBatches}
          batchesLoading={batchesLoading}
          searchText={searchText}
          selectedBatchIds={selectedBatchIds}
          setSelectedBatchIds={setSelectedBatchIds}
          plannerMonth={plannerMonth}
          setPlannerMonth={setPlannerMonth}
          onAssign={handlePlannerAssign}
          onExport={exportPlanner}
        />
      )}

      {activeTab === 2 && (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* LEFT — Batch List */}
          <div style={{ width: 210, flexShrink: 0, background: '#fff', borderRight: '1px solid #d1d5db', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '6px 9px 5px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              <h2 style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .7, color: '#9ca3af', marginBottom: 0 }}>Active Batches</h2>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '3px 4px' }}>
              {batchesLoading ? (
                <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
              ) : filteredBatches.length === 0 ? (
                <div style={{ padding: 12, textAlign: 'center', fontSize: 10, color: '#9ca3af' }}>No batches match</div>
              ) : (
                filteredBatches.map(b => (
                  <BatchCard key={b.id} batch={b} isActive={activeBatchId === b.id} onClick={() => handleSelectBatch(b.id)} />
                ))
              )}
            </div>
          </div>

          {/* RIGHT — Checklist */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {!activeBatchId ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', gap: 8 }}>
                <div style={{ fontSize: 36, opacity: .18 }}>&#128203;</div>
                <span style={{ fontSize: 11 }}>Select a batch to view checklist</span>
              </div>
            ) : checklistLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><Spin /></div>
            ) : checklist ? (
              <>
                <BatchTopBar
                  checklist={checklist}
                  onAutoTick={handleAutoTick}
                  onToggleConditional={handleToggleConditional}
                />
                {(() => {
                  const regularStages = checklist.checklist.filter(s => !s.stage.is_conditional);
                  const conditionalStages = checklist.checklist.filter(s => s.stage.is_conditional);
                  return (
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 28px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 6, alignItems: 'stretch' }}>
                        {regularStages.map((stageData, idx) => (
                          <StageCard key={stageData.stage.id} stageData={stageData} stageNum={idx + 1} onTick={handleTickStep} onUntick={handleUntickStep} tracker={checklist.tracker} />
                        ))}
                      </div>
                      {conditionalStages.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 6, marginTop: 6, alignItems: 'start' }}>
                          {conditionalStages.map((stageData, idx) => (
                            <StageCard key={stageData.stage.id} stageData={stageData} stageNum={regularStages.length + idx + 1} onTick={handleTickStep} onUntick={handleUntickStep} tracker={checklist.tracker} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            ) : null}
          </div>
        </div>
      )}

      {activeTab === 3 && (
        <PendingDocumentsTab
          pendingGrid={pendingGrid}
          pendingGridLoading={pendingGridLoading}
          onJumpToBatch={handleJumpToBatch}
          searchText={searchText}
          onExport={exportPendingDocuments}
        />
      )}

      {activeTab === 4 && (
        <TeamKanbanTab
          teamKanban={teamKanban}
          loading={teamKanbanLoading}
          searchText={searchText}
          onExport={exportTeamView}
        />
      )}

      {activeTab === 5 && (
        <TimelineTab timeline={timeline} loading={timelineLoading} searchText={searchText} onExport={exportTimeline} />
      )}

      {/* ── Create Batch Modal ── */}
      <Modal
        title="Create Dispatch Tracker"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); setNewBatchNo(''); }}
        onOk={handleCreateBatch}
        confirmLoading={createLoading}
        okText="Create"
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: '#4b5563' }}>
          Enter the blend batch number (Gen1 format, e.g., CATCB25007)
        </div>
        <Input
          placeholder="e.g. CATCB25007"
          value={newBatchNo}
          onChange={e => setNewBatchNo(e.target.value.toUpperCase())}
          onPressEnter={handleCreateBatch}
          style={{ fontFamily: 'monospace' }}
        />
      </Modal>

      {/* ── Conditional Toggle Modal ── */}
      <Modal
        title={condModalData?.enable
          ? `Enable ${COND_LABELS[condModalData?.condKey] || ''}`
          : `Remove ${COND_LABELS[condModalData?.condKey] || ''}`}
        open={condModalOpen}
        onCancel={() => { setCondModalOpen(false); setCondModalData(null); }}
        onOk={handleConfirmConditional}
        okText={condModalData?.enable ? 'Enable' : 'Remove'}
        okButtonProps={{ danger: !condModalData?.enable }}
      >
        {condModalData?.enable ? (
          <div>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#4b5563' }}>
              At which stage did the {COND_LABELS[condModalData?.condKey]?.toLowerCase()} occur?
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Select stage..."
              value={condModalData?.stageName || undefined}
              onChange={v => setCondModalData(prev => ({ ...prev, stageName: v }))}
              // All three (DEV, TC/CC, VAL): applicable manufacturing stages in stage_order
              options={condMfgStages.map(s => ({ label: s.stage_name, value: s.stage_name }))}
            />
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 12 }}>
              Removing <b>{COND_LABELS[condModalData?.condKey]}</b> will clear all ticks in this stage. Are you sure?
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4, color: '#374151' }}>Removed By <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                placeholder="Enter your name"
                value={condModalData?.removedBy || ''}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, outline: 'none' }}
                onChange={e => setCondModalData(prev => ({ ...prev, removedBy: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4, color: '#374151' }}>Reason <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea
                placeholder="Why are you removing this conditional?"
                rows={3}
                value={condModalData?.removeReason || ''}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, outline: 'none', resize: 'vertical' }}
                onChange={e => setCondModalData(prev => ({ ...prev, removeReason: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// DISPATCH PLANNER TAB (Tab 1)
// ═══════════════════════════════════════════════════════════
function getWeeksForMonth(yearMonth) {
  // yearMonth = 'YYYY-MM'
  const [year, month] = yearMonth.split('-').map(Number);
  const periodStart = new Date(year, month - 2, 26);
  const periodEnd   = new Date(year, month - 1, 25);
  const dow = periodStart.getDay();
  const back = (dow - 5 + 7) % 7;
  const firstFri = new Date(periodStart);
  firstFri.setDate(firstFri.getDate() - back);
  const weeks = [];
  let ws = new Date(firstFri);
  while (ws <= periodEnd) {
    const we = new Date(ws); we.setDate(we.getDate() + 6);
    const overlapStart = new Date(Math.max(ws.getTime(), periodStart.getTime()));
    const overlapEnd   = new Date(Math.min(we.getTime(), periodEnd.getTime()));
    if (overlapStart <= overlapEnd && Math.round((overlapEnd - overlapStart) / 86400000) + 1 >= 4) {
      const toLocalDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      weeks.push({
        week_num:   weeks.length + 1,
        week_start: toLocalDate(ws),
        week_end:   toLocalDate(we),
      });
    }
    ws.setDate(ws.getDate() + 7);
  }
  return weeks;
}

function fmtWeekDates(weekStart, weekEnd) {
  const fmt = d => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
}

const DispatchPlannerTab = ({
  plannerData, loading, batches, batchesLoading, searchText,
  selectedBatchIds, setSelectedBatchIds, plannerMonth, setPlannerMonth, onAssign, onExport
}) => {
  // Derive 4 months starting from plannerMonth
  const months = React.useMemo(() => {
    const [y, m] = plannerMonth.split('-').map(Number);
    const result = [];
    for (let i = 0; i < 4; i++) {
      let ny = y, nm = m + i;
      while (nm > 12) { nm -= 12; ny++; }
      const key = `${ny}-${String(nm).padStart(2, '0')}`;
      const label = new Date(ny, nm - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
      result.push({ key, label, weeks: getWeeksForMonth(key) });
    }
    return result;
  }, [plannerMonth]);

  // Normalize a dispatch_week_start value (may be full ISO or YYYY-MM-DD) to YYYY-MM-DD
  const normalizeWeekStart = (val) => val ? val.split('T')[0] : null;

  // Build week assignment map from plannerData: week_start → [batch]
  const weekBatchMap = React.useMemo(() => {
    if (!plannerData) return {};
    const map = {};
    plannerData.batches
      .filter(b => b.dispatch_week_start)
      .sort((a, b) => a.dispatch_week_order - b.dispatch_week_order)
      .forEach(b => {
        const key = normalizeWeekStart(b.dispatch_week_start);
        if (!map[key]) map[key] = [];
        map[key].push({ ...b, dispatch_week_start: key });
      });
    return map;
  }, [plannerData]);

  // Unassign a single batch
  const handleUnassign = (batchId) => {
    onAssign([{ id: batchId, dispatch_week_start: null, dispatch_week_order: 0 }]);
  };

  // ── Assign modal ──
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  const [assignModalMonth, setAssignModalMonth] = React.useState('');
  const [assignModalWeek, setAssignModalWeek] = React.useState('');

  const openAssignModal = () => {
    setAssignModalMonth(months[0]?.key || '');
    setAssignModalWeek('');
    setAssignModalOpen(true);
  };

  const assignModalWeeks = React.useMemo(() => {
    if (!assignModalMonth) return [];
    return getWeeksForMonth(assignModalMonth);
  }, [assignModalMonth]);

  const confirmAssign = () => {
    if (!assignModalWeek || selectedBatchIds.length === 0) return;
    const existing = weekBatchMap[assignModalWeek] || [];
    const existingIds = new Set(existing.map(b => b.id));
    const newIds = selectedBatchIds.filter(id => !existingIds.has(id));
    if (newIds.length > 0) {
      const startOrder = existing.length;
      const updates = newIds.map((id, i) => ({
        id,
        dispatch_week_start: assignModalWeek,
        dispatch_week_order: startOrder + i,
      }));
      onAssign(updates);
    }
    setSelectedBatchIds([]);
    setAssignModalOpen(false);
  };

  // ── Reassign modal ──
  const [reassignModalOpen, setReassignModalOpen] = React.useState(false);
  const [reassignMode, setReassignMode] = React.useState('change_week'); // 'change_week' | 'reorder'
  const [reassignMonth, setReassignMonth] = React.useState('');
  const [reassignWeek, setReassignWeek] = React.useState('');
  const [reorderList, setReorderList] = React.useState([]); // [{ id, label }] ordered

  // Planner sidebar uses plannerData.batches (all active trackers) instead of filtered batches
  const sidebarBatches = React.useMemo(() => {
    const source = plannerData?.batches || batches;
    const normalized = source.map(b => ({ ...b, progressPct: b.progressPct ?? b.progress_pct ?? 0 }));
    if (!searchText) return normalized;
    const q = searchText.toLowerCase();
    return normalized.filter(b =>
      (b.parent_blend_batch_no || b.blend_batch_no || b.packing_batch_no || '').toLowerCase().includes(q) ||
      (b.product_name || '').toLowerCase().includes(q)
    );
  }, [plannerData, batches, searchText]);

  // Compute: are all selected batches already assigned?
  const allSelectedAssigned = React.useMemo(() => {
    if (selectedBatchIds.length === 0) return false;
    return selectedBatchIds.every(id => {
      const b = sidebarBatches.find(x => x.id === id);
      return b && normalizeWeekStart(b.dispatch_week_start);
    });
  }, [selectedBatchIds, sidebarBatches]);

  // Assign enabled only when none of the selected are already assigned
  const canAssign = React.useMemo(() => {
    if (selectedBatchIds.length === 0) return false;
    return selectedBatchIds.every(id => {
      const b = sidebarBatches.find(x => x.id === id);
      return b && !normalizeWeekStart(b.dispatch_week_start);
    });
  }, [selectedBatchIds, sidebarBatches]);

  // Are all selected in the same week?
  const selectedAllSameWeek = React.useMemo(() => {
    if (selectedBatchIds.length < 2) return false;
    const weeks = selectedBatchIds.map(id => {
      const b = sidebarBatches.find(x => x.id === id);
      return b ? normalizeWeekStart(b.dispatch_week_start) : null;
    });
    return weeks.every(w => w && w === weeks[0]);
  }, [selectedBatchIds, sidebarBatches]);

  const reassignModalWeeks = React.useMemo(() => {
    if (!reassignMonth) return [];
    return getWeeksForMonth(reassignMonth);
  }, [reassignMonth]);

  const openReassignModal = () => {
    const isSingle = selectedBatchIds.length === 1;
    const defaultMode = (!isSingle && selectedAllSameWeek) ? 'reorder' : 'change_week';
    setReassignMode(defaultMode);
    setReassignMonth(months[0]?.key || '');
    setReassignWeek('');
    // Build reorder list from current week order
    if (!isSingle && selectedAllSameWeek) {
      const b0 = sidebarBatches.find(x => x.id === selectedBatchIds[0]);
      const wk = normalizeWeekStart(b0?.dispatch_week_start);
      const inWeek = (weekBatchMap[wk] || []).filter(b => selectedBatchIds.includes(b.id));
      setReorderList(inWeek.map(b => ({ id: b.id, label: b.parent_blend_batch_no || b.blend_batch_no })));
    } else {
      setReorderList(selectedBatchIds.map(id => {
        const b = sidebarBatches.find(x => x.id === id);
        return { id, label: b ? (b.parent_blend_batch_no || b.blend_batch_no) : id };
      }));
    }
    setReassignModalOpen(true);
  };

  const moveReorderItem = (idx, dir) => {
    setReorderList(prev => {
      const arr = [...prev];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= arr.length) return arr;
      [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
      return arr;
    });
  };

  const confirmReassign = () => {
    if (reassignMode === 'reorder') {
      // Get the week these batches belong to
      const b0 = sidebarBatches.find(x => x.id === reorderList[0]?.id);
      const wk = normalizeWeekStart(b0?.dispatch_week_start);
      if (!wk) return;
      // Reorder within same week: reassign all cards in that week keeping non-selected in place
      const allInWeek = weekBatchMap[wk] || [];
      const nonSelected = allInWeek.filter(b => !selectedBatchIds.includes(b.id));
      // Merge: selected in new order, non-selected appended after
      const merged = [...reorderList.map(r => ({ id: r.id })), ...nonSelected];
      const updates = merged.map((b, i) => ({ id: b.id, dispatch_week_start: wk, dispatch_week_order: i }));
      onAssign(updates);
    } else {
      if (!reassignWeek) return;
      const existing = (weekBatchMap[reassignWeek] || []).filter(b => !selectedBatchIds.includes(b.id));
      const updates = selectedBatchIds.map((id, i) => ({
        id,
        dispatch_week_start: reassignWeek,
        dispatch_week_order: existing.length + i,
      }));
      onAssign(updates);
    }
    setSelectedBatchIds([]);
    setReassignModalOpen(false);
  };

  // Week badge helper
  const getBatchWeekBadge = (batch) => {
    if (!batch.dispatch_week_start) return null;
    const ws = normalizeWeekStart(batch.dispatch_week_start);
    for (const mo of months) {
      const wk = mo.weeks.find(w => w.week_start === ws);
      if (wk) return `W${wk.week_num} · ${mo.label.split(' ')[0]}`;
    }
    // Batch assigned to a week outside current month range
    const d = new Date(ws + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Progress color
  const progColor = pct => pct >= 75 ? '#059669' : pct > 0 ? '#2563eb' : '#d1d5db';

  const prevMonth = () => {
    const [y, m] = plannerMonth.split('-').map(Number);
    let nm = m - 1, ny = y;
    if (nm < 1) { nm = 12; ny--; }
    setPlannerMonth(`${ny}-${String(nm).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const [y, m] = plannerMonth.split('-').map(Number);
    let nm = m + 1, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    setPlannerMonth(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  if (loading && !plannerData) {
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>;
  }

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
      {/* ── LEFT SIDEBAR ── */}
      <div style={{ width: 210, flexShrink: 0, background: '#fff', borderRight: '1px solid #d1d5db', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '6px 9px 5px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <h2 style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .7, color: '#9ca3af', marginBottom: 2 }}>Batches</h2>
          {selectedBatchIds.length > 0 && (
            <div style={{ fontSize: 9, color: '#2563eb', fontWeight: 600 }}>
              {selectedBatchIds.length} selected
              <button onClick={() => setSelectedBatchIds([])} style={{ marginLeft: 6, fontSize: 9, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>✕ clear</button>
            </div>
          )}
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '3px 4px' }}>
          {batchesLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><Spin size="small" /></div>
          ) : sidebarBatches.length === 0 ? (
            <div style={{ padding: 12, textAlign: 'center', fontSize: 10, color: '#9ca3af' }}>No batches</div>
          ) : (
            sidebarBatches.map(b => {
              const isSelected = selectedBatchIds.includes(b.id);
              const weekBadge = getBatchWeekBadge(b);
              const pct = b.progressPct ?? 0;
              return (
                <div
                  key={b.id}
                  onClick={() => setSelectedBatchIds(prev =>
                    prev.includes(b.id) ? prev.filter(x => x !== b.id) : [...prev, b.id]
                  )}
                  style={{
                    border: `1px solid ${isSelected ? '#2563eb' : '#e5e7eb'}`,
                    borderLeft: `3px solid ${isSelected ? '#2563eb' : progColor(pct)}`,
                    borderRadius: 4, padding: '5px 7px', marginBottom: 2, cursor: 'pointer',
                    background: isSelected ? '#eff6ff' : '#fff',
                    transition: 'all .12s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#1e3a5f' }}>
                      {b.parent_blend_batch_no || b.blend_batch_no}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: progColor(pct) }}>{pct}%</span>
                  </div>
                  <div style={{ fontSize: 9, color: '#4b5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 2 }}>{b.product_name}</div>
                  <div style={{ height: 2, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden', marginBottom: weekBadge ? 3 : 0 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: progColor(pct), borderRadius: 2 }} />
                  </div>
                  {weekBadge && (
                    <div style={{ fontSize: 7, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '1px 5px', borderRadius: 3, display: 'inline-block', marginTop: 1 }}>
                      📅 {weekBadge}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── ASSIGN MODAL ── */}
      {assignModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setAssignModalOpen(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '24px 28px', minWidth: 320, boxShadow: '0 8px 32px rgba(0,0,0,.18)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 16 }}>
              Assign {selectedBatchIds.length} batch{selectedBatchIds.length > 1 ? 'es' : ''} to Week
            </div>

            {/* Month dropdown */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 4 }}>Month</label>
              <select
                value={assignModalMonth}
                onChange={e => { setAssignModalMonth(e.target.value); setAssignModalWeek(''); }}
                style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 5, outline: 'none' }}
              >
                {months.map(mo => (
                  <option key={mo.key} value={mo.key}>{mo.label}</option>
                ))}
              </select>
            </div>

            {/* Week dropdown */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 4 }}>Week</label>
              <select
                value={assignModalWeek}
                onChange={e => setAssignModalWeek(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 5, outline: 'none' }}
              >
                <option value="">— select week —</option>
                {assignModalWeeks.map(wk => (
                  <option key={wk.week_start} value={wk.week_start}>
                    Week {wk.week_num} · {fmtWeekDates(wk.week_start, wk.week_end)}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAssignModalOpen(false)}
                style={{ padding: '6px 16px', fontSize: 12, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={confirmAssign}
                disabled={!assignModalWeek}
                style={{
                  padding: '6px 16px', fontSize: 12, fontWeight: 600,
                  background: assignModalWeek ? '#1e3a5f' : '#e5e7eb',
                  color: assignModalWeek ? '#fff' : '#9ca3af',
                  border: 'none', borderRadius: 5, cursor: assignModalWeek ? 'pointer' : 'not-allowed',
                }}
              >Confirm Assign</button>
            </div>
          </div>
        </div>
      )}

      {/* ── REASSIGN MODAL ── */}
      {reassignModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setReassignModalOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '24px 28px', minWidth: 340, boxShadow: '0 8px 32px rgba(0,0,0,.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 16 }}>
              Reassign {selectedBatchIds.length} batch{selectedBatchIds.length > 1 ? 'es' : ''}
            </div>

            {/* Mode toggle — only show for multiple batches in same week */}
            {selectedBatchIds.length > 1 && selectedAllSameWeek && (
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
                {[['reorder', 'Reorder within week'], ['change_week', 'Change week']].map(([val, label]) => (
                  <button key={val} onClick={() => setReassignMode(val)} style={{
                    flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                    background: reassignMode === val ? '#1e3a5f' : '#f9fafb',
                    color: reassignMode === val ? '#fff' : '#4b5563',
                  }}>{label}</button>
                ))}
              </div>
            )}

            {/* Reorder mode */}
            {reassignMode === 'reorder' && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#4b5563', marginBottom: 6 }}>Drag order (top = dispatched first)</div>
                {reorderList.map((item, idx) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', marginBottom: 4, background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 5 }}>
                    <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, minWidth: 16 }}>{idx + 1}.</span>
                    <span style={{ flex: 1, fontSize: 11, fontFamily: 'monospace', fontWeight: 600, color: '#1e3a5f' }}>{item.label}</span>
                    <button onClick={() => moveReorderItem(idx, -1)} disabled={idx === 0}
                      style={{ fontSize: 12, background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#d1d5db' : '#4b5563', padding: '0 3px' }}>↑</button>
                    <button onClick={() => moveReorderItem(idx, 1)} disabled={idx === reorderList.length - 1}
                      style={{ fontSize: 12, background: 'none', border: 'none', cursor: idx === reorderList.length - 1 ? 'default' : 'pointer', color: idx === reorderList.length - 1 ? '#d1d5db' : '#4b5563', padding: '0 3px' }}>↓</button>
                  </div>
                ))}
              </div>
            )}

            {/* Change week mode */}
            {reassignMode === 'change_week' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 4 }}>Month</label>
                  <select value={reassignMonth} onChange={e => { setReassignMonth(e.target.value); setReassignWeek(''); }}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 5, outline: 'none' }}>
                    {months.map(mo => <option key={mo.key} value={mo.key}>{mo.label}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 4 }}>Week</label>
                  <select value={reassignWeek} onChange={e => setReassignWeek(e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 5, outline: 'none' }}>
                    <option value="">— select week —</option>
                    {reassignModalWeeks.map(wk => (
                      <option key={wk.week_start} value={wk.week_start}>
                        Week {wk.week_num} · {fmtWeekDates(wk.week_start, wk.week_end)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setReassignModalOpen(false)}
                style={{ padding: '6px 16px', fontSize: 12, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={confirmReassign}
                disabled={reassignMode === 'change_week' && !reassignWeek}
                style={{
                  padding: '6px 16px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 5,
                  background: (reassignMode === 'reorder' || reassignWeek) ? '#0369a1' : '#e5e7eb',
                  color: (reassignMode === 'reorder' || reassignWeek) ? '#fff' : '#9ca3af',
                  cursor: (reassignMode === 'reorder' || reassignWeek) ? 'pointer' : 'not-allowed',
                }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RIGHT BOARD ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Month nav */}
        <div style={{ background: '#fff', borderBottom: '1px solid #d1d5db', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button onClick={prevMonth} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#4b5563' }}>◀</button>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', minWidth: 130 }}>
            {months[0]?.label} – {months[3]?.label}
          </span>
          <button onClick={nextMonth} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#4b5563' }}>▶</button>
          <button
            onClick={openAssignModal}
            disabled={!canAssign}
            style={{
              marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '3px 14px',
              background: canAssign ? '#1e3a5f' : '#e5e7eb',
              color: canAssign ? '#fff' : '#9ca3af',
              border: 'none', borderRadius: 5, cursor: canAssign ? 'pointer' : 'not-allowed',
              transition: 'all .15s',
            }}
          >
            Assign{canAssign ? ` (${selectedBatchIds.length})` : ''}
          </button>
          <button
            onClick={openReassignModal}
            disabled={!allSelectedAssigned}
            style={{
              fontSize: 11, fontWeight: 600, padding: '3px 14px',
              background: allSelectedAssigned ? '#0369a1' : '#e5e7eb',
              color: allSelectedAssigned ? '#fff' : '#9ca3af',
              border: 'none', borderRadius: 5, cursor: allSelectedAssigned ? 'pointer' : 'not-allowed',
              transition: 'all .15s',
            }}
          >
            Reassign{allSelectedAssigned ? ` (${selectedBatchIds.length})` : ''}
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: 9, color: '#4b5563' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, background: '#059669', borderRadius: 2, display: 'inline-block' }} /> ≥75%</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, background: '#2563eb', borderRadius: 2, display: 'inline-block' }} /> In progress</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, background: '#d1d5db', border: '1px solid #9ca3af', borderRadius: 2, display: 'inline-block' }} /> Not started</span>
            {onExport && <button onClick={onExport} style={{ padding: '3px 12px', fontSize: 10, fontWeight: 700, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>⬇ Export</button>}
          </div>
        </div>

        {/* Board columns */}
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', display: 'flex', alignItems: 'stretch', padding: '10px 14px 16px', gap: 8 }}>
          {months.map(mo => (
            <React.Fragment key={mo.key}>
              {/* Month label above its weeks */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: .5, paddingLeft: 2, flexShrink: 0 }}>{mo.label}</div>
                <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                  {mo.weeks.map(wk => {
                    const cards = weekBatchMap[wk.week_start] || [];
                    return (
                      <div
                        key={wk.week_start}
                        style={{ width: 185, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
                      >
                        {/* Week header */}
                        <div
                          style={{
                            background: '#fff',
                            border: '1px solid #d1d5db',
                            borderBottom: 'none', borderRadius: '5px 5px 0 0',
                            padding: '6px 10px',
                          }}
                        >
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#1e3a5f' }}>
                            Week {wk.week_num}
                          </div>
                          <div style={{ fontSize: 8, color: '#9ca3af', fontFamily: 'monospace', marginTop: 1 }}>
                            {fmtWeekDates(wk.week_start, wk.week_end)}
                          </div>
                        </div>
                        {/* Week body */}
                        <div
                          style={{ flex: 1, background: '#f8f9fb', border: '1px solid #d1d5db', borderRadius: '0 0 5px 5px', padding: 6, minHeight: 200, overflowY: 'auto' }}
                        >
                          {cards.length === 0 ? (
                            <div style={{ fontSize: 9, color: '#9ca3af', textAlign: 'center', padding: '14px 6px', fontStyle: 'italic' }}>No batches assigned</div>
                          ) : (
                            cards.map(b => {
                              const pct = b.progress_pct ?? 0;
                              return (
                                <div
                                  key={b.id}
                                  style={{
                                    background: '#fff', border: '1px solid #e5e7eb',
                                    borderLeft: `3px solid ${progColor(pct)}`,
                                    borderRadius: 4, padding: '5px 7px', marginBottom: 4,
                                    boxShadow: '0 1px 2px rgba(0,0,0,.06)',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#1e3a5f' }}>
                                      {b.parent_blend_batch_no || b.blend_batch_no}
                                    </span>
                                    <button
                                      onClick={e => { e.stopPropagation(); handleUnassign(b.id); }}
                                      style={{ fontSize: 8, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}
                                    >✕</button>
                                  </div>
                                  <div style={{ fontSize: 8, color: '#4b5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 2 }}>{b.product_name}</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <div style={{ flex: 1, height: 2, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${pct}%`, background: progColor(pct) }} />
                                    </div>
                                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: progColor(pct), fontWeight: 700 }}>{pct}%</span>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// BATCH CARD (Left panel)
// ═══════════════════════════════════════════════════════════
const BatchCard = ({ batch, isActive, onClick }) => {
  const isDone = batch.progressPct === 100;
  const isSplit = !!batch.strength_code;
  const parentNo = batch.parent_blend_batch_no || batch.blend_batch_no;
  return (
    <div onClick={onClick} style={{
      border: `1px solid ${isActive ? '#2563eb' : '#d1d5db'}`,
      borderLeft: `3px solid ${isActive ? '#2563eb' : isDone ? '#059669' : 'transparent'}`,
      borderRadius: 4, padding: '5px 7px', marginBottom: 2, cursor: 'pointer',
      transition: 'all .12s', background: isActive ? '#eff6ff' : '#fff',
      opacity: isDone ? 0.5 : 1
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#1e3a5f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{parentNo}</span>
          {isSplit && (
            <span style={{ fontSize: 7, fontWeight: 700, padding: '1px 4px', borderRadius: 2, background: '#dbeafe', color: '#1d4ed8', whiteSpace: 'nowrap', flexShrink: 0 }}>{batch.strength_code}</span>
          )}
        </div>
        <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#2563eb', flexShrink: 0, marginLeft: 4 }}>{batch.progressPct}%</span>
      </div>
      {isSplit && (
        <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#6b7280', marginBottom: 1 }}>{batch.blend_batch_no}</div>
      )}
      <div style={{ fontSize: 9, color: '#4b5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>
        {batch.product_name || '--'}
      </div>
      <div style={{ height: 2, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden', marginBottom: 2 }}>
        <div style={{ height: '100%', background: '#2563eb', borderRadius: 2, width: `${batch.progressPct}%`, transition: 'width .3s' }} />
      </div>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {batch.dispatch_week_start && (
          <span style={{ fontSize: 6, fontWeight: 700, padding: '1px 4px', borderRadius: 2, background: '#e0f2fe', color: '#0369a1' }}>
            {(() => { const d = new Date((batch.dispatch_week_start || '').split('T')[0] + 'T00:00:00'); return `W${Math.ceil(d.getDate()/7)} · ${d.toLocaleString('en-IN',{month:'short'})}`; })()}
          </span>
        )}
        {batch.hasDeviation && <span style={{ fontSize: 6, fontWeight: 700, padding: '1px 4px', borderRadius: 2, textTransform: 'uppercase', background: '#fee2e2', color: '#991b1b' }}>DEV</span>}
        {batch.hasTcc && <span style={{ fontSize: 6, fontWeight: 700, padding: '1px 4px', borderRadius: 2, textTransform: 'uppercase', background: '#fef3c7', color: '#92400e' }}>TC/CC</span>}
        {batch.hasValidation && <span style={{ fontSize: 6, fontWeight: 700, padding: '1px 4px', borderRadius: 2, textTransform: 'uppercase', background: '#ede9fe', color: '#5b21b6' }}>VAL</span>}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// BATCH TOP BAR
// ═══════════════════════════════════════════════════════════
const BatchTopBar = ({ checklist, onAutoTick, onToggleConditional }) => {
  const { tracker, progress } = checklist;
  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #d1d5db', padding: '6px 14px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>
              {tracker.parent_blend_batch_no || tracker.blend_batch_no}
            </span>
            {tracker.strength_code && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: '#dbeafe', color: '#1d4ed8' }}>
                {tracker.strength_code}
              </span>
            )}
          </div>
          {tracker.strength_code && (
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#6b7280' }}>{tracker.blend_batch_no}</div>
          )}
          <div style={{ fontSize: 11, color: '#4b5563' }}>{tracker.product_name}</div>
        </div>
        <div style={{ width: 1, height: 18, background: '#d1d5db' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 90, height: 4, background: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 2, background: '#2563eb', width: `${progress.pct}%`, transition: 'width .3s' }} />
          </div>
          <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#2563eb' }}>{progress.pct}%</span>
          <span style={{ fontSize: 9, color: '#9ca3af' }}>({progress.done}/{progress.total})</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {tracker.dispatch_week_start && (
          <span style={{ fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 3, background: '#e0f2fe', color: '#0369a1' }}>
            📅 {(() => { const d = new Date((tracker.dispatch_week_start || '').split('T')[0] + 'T00:00:00'); return `W${Math.ceil(d.getDate()/7)} · ${d.toLocaleString('en-IN',{month:'short',year:'numeric'})}`; })()}
          </span>
        )}
        <button onClick={onAutoTick} style={{
          fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 3,
          border: '1.5px solid #2563eb', background: '#eff6ff', color: '#2563eb', cursor: 'pointer'
        }}>Auto-Tick</button>
        <div style={{ width: 1, height: 14, background: '#d1d5db' }} />
        <span style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, color: '#9ca3af' }}>Conditional:</span>
        {['dev', 'tcc', 'val'].map(key => {
          const isOn = key === 'dev' ? tracker.has_deviation : key === 'tcc' ? tracker.has_tcc : tracker.has_validation;
          const colors = COND_COLORS[key];
          return (
            <button key={key} onClick={() => onToggleConditional(key)} style={{
              fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
              border: `1.5px solid ${isOn ? colors.border : '#d1d5db'}`,
              background: isOn ? colors.bg : '#f9fafb',
              color: isOn ? colors.color : '#4b5563',
              transition: 'all .12s'
            }}>
              {key === 'dev' ? 'DEV' : key === 'tcc' ? 'TC/CC' : 'VAL'}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// STAGE CARD
// ═══════════════════════════════════════════════════════════
const StageCard = ({ stageData, stageNum, onTick, onUntick, tracker }) => {
  const { stage, steps: stageSteps } = stageData;
  const tickedCount = stageSteps.filter(s => s.isTicked).length;
  const isDone = tickedCount === stageSteps.length && stageSteps.length > 0;
  const condKey = stage.condition_key;
  const condColors = condKey ? COND_COLORS[condKey] : null;
  const condStageName = condKey === 'dev' ? tracker?.deviation_stage
    : condKey === 'tcc' ? tracker?.tcc_stage
    : condKey === 'val' ? tracker?.validation_stage
    : null;

  const headerBg = isDone ? '#065f46' : condColors ? condColors.headerBg : '#1e3a5f';
  const borderColor = isDone ? '#6ee7b7' : condColors ? condColors.border : '#d1d5db';

  return (
    <div style={{ background: '#fff', borderRadius: 5, border: `1px solid ${borderColor}`, boxShadow: '0 1px 3px rgba(0,0,0,.07)', overflow: 'hidden', display: 'flex', flexDirection: 'column', ...(condKey ? {} : { height: 180 }) }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', background: headerBg, color: '#fff', minHeight: 30, padding: 0, flexShrink: 0 }}>
        <div style={{ width: 28, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 9, fontWeight: 800, borderRight: '1px solid rgba(255,255,255,.12)', flexShrink: 0, color: 'rgba(255,255,255,.5)' }}>{stageNum}</div>
        <div style={{ flex: 1, padding: '0 7px', lineHeight: 1.3, overflow: 'hidden' }}>
          <div style={{ fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {stage.stage_name}
            {condKey && <span style={{ fontSize: 6, fontWeight: 700, textTransform: 'uppercase', background: 'rgba(255,255,255,.2)', padding: '1px 4px', borderRadius: 2, marginLeft: 4 }}>
              {condKey === 'dev' ? 'DEV' : condKey === 'tcc' ? 'TC/CC' : 'VAL'}
            </span>}
          </div>
          {condKey && condStageName && (
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              @ {condStageName}
            </div>
          )}
        </div>
        <div style={{ paddingRight: 8, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(255,255,255,.5)' }}>{tickedCount}/{stageSteps.length}</span>
          {isDone && <span style={{ fontSize: 10 }}>&#9989;</span>}
        </div>
      </div>

      {/* Steps — fixed height + scroll for regular; grow naturally for conditionals */}
      <div style={{ flex: 1, overflowY: 'auto', background: '#f8f9fb' }}>
        {(() => {
          // Compute last ticked step in this stage for sequential untick
          const tickedStepsInStage = stageSteps.filter(s => s.isTicked);
          const lastTickedStepId = tickedStepsInStage.length > 0
            ? tickedStepsInStage[tickedStepsInStage.length - 1].id
            : null;
          return stageSteps.map(step => (
            <StepRow key={step.id} step={{ ...step, canUntick: step.isTicked && step.id === lastTickedStepId }} onTick={onTick} onUntick={onUntick} />
          ));
        })()}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// STEP ROW
// ═══════════════════════════════════════════════════════════
const StepRow = ({ step, onTick, onUntick }) => {
  const { isTicked, isLocked, is_auto, tick, canUntick } = step;

  let rowBg = '#fff';
  if (isTicked) rowBg = '#f0fdf4';
  else if (isLocked) rowBg = '#fafafa';
  else if (is_auto && !isTicked) rowBg = '#f0f7ff';

  // Checkbox
  let cbStyle, cbText, cbClick = null;
  if (is_auto && !isTicked) {
    cbStyle = { background: '#dbeafe', border: '1.5px solid #93c5fd', color: '#2563eb', cursor: 'default' };
    cbText = '\u26A1';
  } else if (isTicked) {
    cbStyle = { background: '#059669', border: '1.5px solid #059669', color: '#fff', cursor: 'pointer' };
    cbText = '\u2713';
    cbClick = () => {
      if (!canUntick) {
        message.warning('Steps must be unticked sequentially. Please untick the last completed step in this stage first.');
        return;
      }
      let untickName = '';
      let untickReason = '';
      Modal.confirm({
        title: 'Untick Step',
        width: 420,
        content: (
          <div style={{ marginTop: 8 }}>
            <p style={{ marginBottom: 12, color: '#374151' }}>Are you sure you want to untick <b>"{step.step_name}"</b>?</p>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4, color: '#374151' }}>Unticked By <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                placeholder="Enter your name"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, outline: 'none' }}
                onChange={e => { untickName = e.target.value; }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4, color: '#374151' }}>Reason <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea
                placeholder="Why are you unchecking this step?"
                rows={3}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, outline: 'none', resize: 'vertical' }}
                onChange={e => { untickReason = e.target.value; }}
              />
            </div>
          </div>
        ),
        okText: 'Untick',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          if (!untickName.trim()) {
            message.warning('Please enter who is unticking');
            return Promise.reject();
          }
          if (!untickReason.trim()) {
            message.warning('Please enter a reason for unticking');
            return Promise.reject();
          }
          return onUntick(step.id, untickName.trim(), untickReason.trim());
        }
      });
    };
  } else if (isLocked) {
    cbStyle = { background: '#f3f4f6', border: '1.5px solid #d1d5db', color: '#9ca3af', cursor: 'not-allowed' };
    cbText = '\uD83D\uDD12';
  } else {
    cbStyle = { background: '#fff', border: '1.5px solid #2563eb', color: 'transparent', cursor: 'pointer' };
    cbText = '\u2713';
    cbClick = () => onTick(step.id);
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '20px 22px 1fr auto',
      alignItems: 'center', borderBottom: '1px solid #e5e7eb',
      background: rowBg, minHeight: 28, transition: 'background .1s'
    }}>
      {/* Indent bar */}
      <div style={{ gridColumn: 1, gridRow: '1/3', alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #e5e7eb' }}>
        <div style={{ width: 2, height: '65%', borderRadius: 1, background: '#d1d5db' }} />
      </div>

      {/* Checkbox */}
      <div onClick={cbClick} style={{
        gridColumn: 2, gridRow: '1/3',
        width: 14, height: 14, borderRadius: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 8, fontWeight: 800, margin: '0 4px', transition: 'all .12s', flexShrink: 0,
        ...cbStyle
      }}
        onMouseEnter={e => {
          if (cbClick && isTicked) { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#fff'; }
          else if (cbClick) { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }
        }}
        onMouseLeave={e => {
          if (cbClick && isTicked) { e.currentTarget.style.background = '#059669'; e.currentTarget.style.borderColor = '#059669'; e.currentTarget.style.color = '#fff'; }
          else if (cbClick) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = 'transparent'; }
        }}
      >{cbText}</div>

      {/* Step info */}
      <div style={{ gridColumn: 3, gridRow: 1, padding: '4px 4px 0', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, color: '#9ca3af', flexShrink: 0 }}>#{step.id}</span>
          <Tooltip title={step.step_name}>
            <span style={{ fontSize: 10, fontWeight: 600, color: isLocked ? '#9ca3af' : '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{step.step_name}</span>
          </Tooltip>
        </div>
      </div>

      {/* Team dot */}
      <Tooltip title={step.team}>
        <div style={{ gridColumn: 4, gridRow: '1/3', width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: getTeamColor(step.team), margin: '0 6px' }} />
      </Tooltip>

      {/* Timestamp row */}
      <div style={{ gridColumn: '3/5', gridRow: 2, padding: '0 4px 3px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        {isTicked && tick ? (
          <>
            <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#9ca3af', whiteSpace: 'nowrap' }}>{formatDate(tick.ticked_at)}</span>
            {tick.previous_step_completed_at && (
              <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#059669', whiteSpace: 'nowrap' }}>{formatElapsed(tick.previous_step_completed_at, tick.ticked_at)}</span>
            )}
          </>
        ) : isLocked && step.dependency_step_ids?.length > 0 ? (
          <span style={{ fontSize: 7, color: '#9ca3af', whiteSpace: 'nowrap' }}>Awaiting #{step.dependency_step_ids.join(', #')}</span>
        ) : null}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// PENDING DOCUMENTS TAB
// ═══════════════════════════════════════════════════════════
const PendingDocumentsTab = ({
  pendingGrid, pendingGridLoading, onJumpToBatch, searchText, onExport
}) => {
  if (!pendingGrid) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>;

  const rawGrid = pendingGrid.grid || [];
  const grid = searchText
    ? rawGrid.filter(row => {
        const q = searchText.toLowerCase();
        return (row.tracker.blend_batch_no || '').toLowerCase().includes(q)
          || (row.tracker.parent_blend_batch_no || '').toLowerCase().includes(q)
          || (row.tracker.product_name || '').toLowerCase().includes(q);
      })
    : rawGrid;
  const allSteps = pendingGrid.steps || [];
  const allStages = pendingGrid.stages || [];

  const stageStepMap = {};
  allStages.forEach(s => { stageStepMap[s.id] = []; });
  allSteps.forEach(s => { if (stageStepMap[s.dispatch_stage_id]) stageStepMap[s.dispatch_stage_id].push(s); });

  const stageFirstStepIds = new Set();
  allStages.forEach(s => { const st = stageStepMap[s.id]; if (st && st.length) stageFirstStepIds.add(st[0].id); });

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Legend */}
      <div style={{ padding: '5px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#4b5563' }}>
          <div style={{ width: 11, height: 11, borderRadius: 2, background: '#2563eb' }} />
          Pending step (click to go to checklist)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#4b5563' }}>
          <div style={{ width: 11, height: 11, borderRadius: 2, background: '#f3f4f6', border: '1px solid #d1d5db' }} />
          Completed or N/A (not shown)
        </div>
        {onExport && <button onClick={onExport} style={{ marginLeft: 'auto', padding: '3px 12px', fontSize: 10, fontWeight: 700, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>⬇ Export</button>}
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 14px 28px' }}>
        {pendingGridLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : (
          <div style={{ overflowX: 'auto', borderRadius: 5, boxShadow: '0 1px 3px rgba(0,0,0,.07)' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 10, whiteSpace: 'nowrap' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ textAlign: 'left', minWidth: 150, position: 'sticky', left: 0, zIndex: 20, background: '#1e3a5f', padding: '5px 10px', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,.9)' }} rowSpan={2}>Batch / Product</th>
                  {allStages.map(stage => {
                    const stepCount = (stageStepMap[stage.id] || []).length;
                    if (!stepCount) return null;
                    return <th key={stage.id} colSpan={stepCount} style={{ padding: '4px 6px', fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,.9)', textAlign: 'center', letterSpacing: .2, borderLeft: '2px solid rgba(255,255,255,.25)', background: '#1e3a5f' }}>{stage.stage_name}</th>;
                  })}
                  <th rowSpan={2} style={{ minWidth: 56, textAlign: 'center', background: '#243f6a', position: 'sticky', right: 0, zIndex: 20, color: 'rgba(255,255,255,.9)', fontSize: 9, fontWeight: 700, padding: '5px 8px' }}>Pending</th>
                </tr>
                <tr>
                  {allSteps.map(step => {
                    const isFirst = stageFirstStepIds.has(step.id);
                    return (
                      <th key={step.id} style={{
                        padding: '4px 4px', minWidth: 64, maxWidth: 82,
                        borderLeft: isFirst ? '2px solid rgba(255,255,255,.3)' : '1px solid rgba(255,255,255,.08)',
                        verticalAlign: 'top', textAlign: 'left', background: '#162d4a'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span style={{ fontSize: 8, color: '#fff', fontWeight: 600, lineHeight: 1.25, whiteSpace: 'normal', maxWidth: 78 }}>{step.step_name}</span>
                          <span style={{ fontSize: 7, color: 'rgba(255,255,255,.45)', marginTop: 1 }}>{step.team}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grid.map((row, rowIdx) => {
                  const pendingCount = row.stepStates.filter(ss => ss.state === 'pending').length;
                  return (
                    <tr key={row.tracker.id} style={{ background: rowIdx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{
                        textAlign: 'left', padding: '4px 9px', background: '#fff',
                        fontWeight: 700, fontFamily: 'monospace', fontSize: 9,
                        color: '#1e3a5f', position: 'sticky', left: 0, zIndex: 5,
                        borderRight: '2px solid #d1d5db', whiteSpace: 'nowrap',
                        border: '1px solid #e5e7eb'
                      }}>
                        {row.tracker.blend_batch_no}
                        <div style={{ fontSize: 8, fontWeight: 400, color: '#4b5563', fontFamily: 'sans-serif', marginTop: 1 }}>{row.tracker.product_name}</div>
                      </td>
                      {row.stepStates.map(ss => {
                        const isFirst = stageFirstStepIds.has(ss.stepId);
                        const isPending = ss.state === 'pending';
                        return (
                          <td key={ss.stepId} style={{
                            padding: '2px 3px', border: '1px solid #e5e7eb',
                            textAlign: 'center', verticalAlign: 'middle',
                            borderLeft: isFirst ? '2px solid #d1d5db' : undefined,
                            background: rowIdx % 2 === 0 ? '#fff' : '#fafbfc',
                          }}>
                            {isPending ? (
                              <div
                                onClick={() => onJumpToBatch(row.tracker.id)}
                                style={{
                                  width: '100%', minHeight: 26, borderRadius: 3,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: '#2563eb', color: '#fff',
                                  fontSize: 11, cursor: 'pointer',
                                  transition: 'opacity .1s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '.8'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                              >
                                ⬜
                              </div>
                            ) : (
                              <div style={{ minHeight: 26 }} />
                            )}
                          </td>
                        );
                      })}
                      {/* Pending count summary */}
                      <td style={{
                        textAlign: 'center', padding: '2px 7px', whiteSpace: 'nowrap', background: '#fff',
                        position: 'sticky', right: 0, borderLeft: '2px solid #d1d5db', border: '1px solid #e5e7eb'
                      }}>
                        {pendingCount > 0
                          ? <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#2563eb' }}>{pendingCount} ⬜</span>
                          : <span style={{ fontSize: 9, color: '#9ca3af' }}>—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// TEAM KANBAN TAB (Tab 3)
// ═══════════════════════════════════════════════════════════
const TEAM_LANE_COLORS = {
  'Production':  { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', headerBg: '#78350f' },
  'QA':          { bg: '#f5f3ff', border: '#c4b5fd', color: '#5b21b6', headerBg: '#4c1d95' },
  'QA Dispatch': { bg: '#eff6ff', border: '#93c5fd', color: '#1d4ed8', headerBg: '#1e3a5f' },
  'QC':          { bg: '#f0fdf4', border: '#6ee7b7', color: '#065f46', headerBg: '#064e3b' },
  'Automated':   { bg: '#f0f7ff', border: '#93c5fd', color: '#1d4ed8', headerBg: '#1e40af' },
  'Engineering': { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', headerBg: '#7f1d1d' },
  'Validation':  { bg: '#eef2ff', border: '#a5b4fc', color: '#3730a3', headerBg: '#312e81' },
  'MQA':         { bg: '#fdf4ff', border: '#e879f9', color: '#86198f', headerBg: '#701a75' },
  'AQA':         { bg: '#fff7ed', border: '#fdba74', color: '#9a3412', headerBg: '#7c2d12' },
  'TT':          { bg: '#f0fdfa', border: '#5eead4', color: '#0f766e', headerBg: '#134e4a' },
};

const ALL_TEAMS = ['Production', 'QA', 'QA Dispatch', 'QC', 'Engineering', 'Validation', 'MQA', 'AQA', 'TT', 'Automated'];

const TeamKanbanTab = ({ teamKanban, loading, searchText, onExport }) => {
  if (loading) return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>;

  // Build map from API data applying search filter
  const teamMap = {};
  (teamKanban || []).forEach(({ team, steps }) => {
    const filtered = searchText
      ? steps.filter(s => {
          const q = searchText.toLowerCase();
          const batchNo = (s.parent_blend_batch_no || s.blend_batch_no).toLowerCase();
          const prod = (s.product_name || '').toLowerCase();
          return batchNo.includes(q) || prod.includes(q);
        })
      : steps;
    teamMap[team] = filtered;
  });

  // Format elapsed time as "Xh Ym"
  const fmtElapsed = (since) => {
    if (!since) return null;
    const diffMs = Date.now() - new Date(since).getTime();
    if (diffMs < 0) return null;
    const totalMins = Math.floor(diffMs / 60000);
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
  };

  // Color timer by urgency
  const timerColor = (since) => {
    if (!since) return '#6b7280';
    const hrs = (Date.now() - new Date(since).getTime()) / 3600000;
    if (hrs < 24)  return '#16a34a';  // green < 1 day
    if (hrs < 72)  return '#d97706';  // orange 1–3 days
    return '#dc2626';                  // red > 3 days
  };

  const openBatch = (batchId) => window.open(`/dispatch-tracker?batchId=${batchId}`, '_blank');

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '5px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {onExport && <button onClick={onExport} style={{ padding: '3px 12px', fontSize: 10, fontWeight: 700, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>⬇ Export</button>}
      </div>
      {/* Kanban lanes */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', display: 'flex', alignItems: 'stretch', padding: '10px 14px', gap: 10 }}>
        {ALL_TEAMS.map(team => {
          const steps = teamMap[team] || [];
          const colors = TEAM_LANE_COLORS[team] || { bg: '#f9fafb', border: '#d1d5db', color: '#374151', headerBg: '#374151' };
          const isEmpty = steps.length === 0;
          const actionableCount = steps.filter(s => s.is_actionable).length;

          return (
            <div key={team} style={{
              width: 230, flexShrink: 0, display: 'flex', flexDirection: 'column',
              background: isEmpty ? '#fafafa' : '#fff',
              border: `1px solid ${isEmpty ? '#e5e7eb' : colors.border}`,
              borderRadius: 8, overflow: 'hidden', opacity: isEmpty ? 0.55 : 1,
            }}>
              {/* Lane header */}
              <div style={{ background: isEmpty ? '#9ca3af' : colors.headerBg, color: '#fff', padding: '9px 12px', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700 }}>{team}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,.25)', borderRadius: 10, padding: '1px 8px' }}>
                    {steps.length}
                  </span>
                </div>
                {!isEmpty && (
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>
                    {actionableCount} actionable · {steps.length - actionableCount} coming
                  </div>
                )}
              </div>

              {/* Step cards — one per step */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '6px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {isEmpty ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', fontSize: 10, color: '#9ca3af' }}>No pending work</div>
                ) : steps.map((step, idx) => {
                  const elapsed = fmtElapsed(step.actionable_since);
                  const tColor = timerColor(step.actionable_since);
                  const batchNo = step.parent_blend_batch_no || step.blend_batch_no;
                  return (
                    <div
                      key={`${step.batch_id}-${step.step_id}`}
                      onClick={() => openBatch(step.batch_id)}
                      style={{
                        background: step.is_actionable ? '#fff' : '#fafafa',
                        border: `1px solid ${step.is_actionable ? colors.border : '#e5e7eb'}`,
                        borderLeft: `3px solid ${step.is_actionable ? colors.headerBg : '#d1d5db'}`,
                        borderRadius: 5, padding: '7px 9px', cursor: 'pointer',
                        transition: 'box-shadow .12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,.1)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      {/* Row 1: batch no + timer */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#1e3a5f' }}>
                            {batchNo}
                          </span>
                          {step.strength_code && (
                            <span style={{ fontSize: 7, fontWeight: 700, padding: '1px 4px', borderRadius: 2, background: '#dbeafe', color: '#1d4ed8' }}>
                              {step.strength_code}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: elapsed ? tColor : '#9ca3af', flexShrink: 0, marginLeft: 4 }}>
                          {elapsed || '—'}
                        </span>
                      </div>
                      {/* Row 2: product name */}
                      <div style={{ fontSize: 9, color: '#6b7280', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {step.product_name}
                      </div>
                      {/* Row 3: step name bold */}
                      <div style={{ fontSize: 10, fontWeight: 700, color: step.is_actionable ? '#111827' : '#9ca3af' }}>
                        {step.step_name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// TIMELINE TAB (Tab 4) — Pivot table: Batches × Steps (hours)
// ═══════════════════════════════════════════════════════════
const TimelineTab = ({ timeline, loading, searchText, onExport }) => {

  if (loading) {
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>;
  }

  if (!timeline || timeline.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
        No batches found for the selected filters
      </div>
    );
  }

  // ── Build ordered column list from all batches ──
  // Use a Map keyed by step_id to preserve first-seen order
  const colMap = new Map(); // step_id → { step_id, step_name, team, stage_name, stage_order, step_order }
  for (const batch of timeline) {
    for (const stage of batch.stages) {
      for (const step of stage.steps) {
        if (!colMap.has(step.id)) {
          colMap.set(step.id, {
            step_id: step.id,
            step_name: step.step_name,
            team: step.team,
            stage_name: stage.stage_name,
            stage_order: stage.stage_order,
            step_order: step.step_order,
          });
        }
      }
    }
  }
  const columns = Array.from(colMap.values()).sort((a, b) =>
    a.stage_order !== b.stage_order ? a.stage_order - b.stage_order : a.step_order - b.step_order
  );

  // ── Build per-batch lookup: stepId → { wait_days, is_waiting, ticked_at } ──
  const batchStepMap = {}; // batchId → { stepId → stepData }
  for (const batch of timeline) {
    batchStepMap[batch.id] = {};
    for (const stage of batch.stages) {
      for (const step of stage.steps) {
        batchStepMap[batch.id][step.id] = step;
      }
    }
  }

  // ── Filter ──
  const filtered = searchText
    ? timeline.filter(b => {
        const q = searchText.toLowerCase();
        return (b.parent_blend_batch_no || b.blend_batch_no).toLowerCase().includes(q)
          || (b.product_name || '').toLowerCase().includes(q);
      })
    : timeline;

  // ── Cell value: hours (wait_days * 24, or for sub-day use hours from ms) ──
  // wait_days is in days — display as hours (1 decimal)
  const cellHours = (step) => {
    if (!step) return null;
    if (step.wait_days == null) return null;
    return (step.wait_days * 24).toFixed(1);
  };

  const cellStyle = (step) => {
    if (!step || step.wait_days == null) return { color: '#d1d5db', bg: '#fff' };
    if (step.is_waiting) return { color: '#b45309', bg: '#fef3c7' }; // pending/waiting
    const d = step.wait_days;
    if (d <= 1)  return { color: '#065f46', bg: '#d1fae5' };
    if (d <= 3)  return { color: '#d97706', bg: '#fef9c3' };
    return { color: '#dc2626', bg: '#fee2e2' };
  };

  // Group columns by stage for the stage header row
  const stageGroups = [];
  for (const col of columns) {
    const last = stageGroups[stageGroups.length - 1];
    if (last && last.stage_name === col.stage_name) {
      last.count++;
    } else {
      stageGroups.push({ stage_name: col.stage_name, count: 1 });
    }
  }

  const BATCH_COL_W = 160;
  const STEP_COL_W  = 90;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f9fafb' }}>
      {/* Info bar */}
      <div style={{ padding: '5px 16px', borderBottom: '1px solid #e5e7eb', background: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>{filtered.length} batch{filtered.length !== 1 ? 'es' : ''}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 9 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ color: '#059669', fontWeight: 700, fontSize: 11 }}>✓</span><span style={{ color: '#059669', fontWeight: 600 }}>Done</span></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ color: '#b45309', fontSize: 11 }}>⏳</span><span style={{ color: '#b45309', fontWeight: 600 }}>Awaiting Action</span></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ color: '#d1d5db', fontSize: 11 }}>○</span><span style={{ color: '#9ca3af', fontWeight: 600 }}>Not Yet Actionable</span></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ color: '#d1d5db', fontSize: 11 }}>—</span><span style={{ color: '#9ca3af', fontWeight: 600 }}>N/A</span></span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 9, color: '#9ca3af' }}>Hours since previous step completed:</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: '#d1fae5', border: '1px solid #065f46', display: 'inline-block' }} />
            <span style={{ color: '#065f46', fontWeight: 600 }}>≤ 24h</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: '#fef9c3', border: '1px solid #d97706', display: 'inline-block' }} />
            <span style={{ color: '#d97706', fontWeight: 600 }}>1–3 days</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: '#fee2e2', border: '1px solid #dc2626', display: 'inline-block' }} />
            <span style={{ color: '#dc2626', fontWeight: 600 }}>&gt; 3 days</span>
          </span>
          {onExport && <button onClick={onExport} style={{ marginLeft: 12, padding: '3px 12px', fontSize: 10, fontWeight: 700, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>⬇ Export</button>}
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: BATCH_COL_W + columns.length * STEP_COL_W }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
            {/* Row 1: Stage group headers */}
            <tr>
              <th rowSpan={3} style={{
                width: BATCH_COL_W, minWidth: BATCH_COL_W,
                background: '#1e3a5f', color: '#fff',
                padding: '8px 12px', textAlign: 'left',
                fontSize: 11, fontWeight: 700,
                border: '1px solid #2d4f7c',
                position: 'sticky', left: 0, top: 0, zIndex: 5,
              }}>
                Batch
              </th>
              {stageGroups.map((sg, i) => (
                <th key={i} colSpan={sg.count} style={{
                  background: '#1e3a5f', color: '#fff',
                  padding: '7px 8px', textAlign: 'center',
                  fontSize: 11, fontWeight: 700,
                  border: '1px solid #2d4f7c',
                  whiteSpace: 'nowrap',
                  position: 'sticky', top: 0,
                  borderBottom: '2px solid #3b82f6',
                }}>
                  {sg.stage_name}
                </th>
              ))}
            </tr>
            {/* Row 2: Step names */}
            <tr>
              {columns.map(col => (
                <th key={col.step_id} style={{
                  width: STEP_COL_W, minWidth: STEP_COL_W,
                  background: '#243f6a', color: '#e0eaff',
                  padding: '5px 6px', textAlign: 'center',
                  fontSize: 10, fontWeight: 600,
                  border: '1px solid #2d4f7c',
                  whiteSpace: 'normal', wordBreak: 'break-word',
                  lineHeight: 1.3,
                  position: 'sticky', top: 0,
                }}>
                  {col.step_name}
                </th>
              ))}
            </tr>
            {/* Row 3: Department/team names */}
            <tr>
              {columns.map(col => (
                <th key={col.step_id} style={{
                  background: '#1a3357', color: '#93c5fd',
                  padding: '4px 6px', textAlign: 'center',
                  fontSize: 9, fontWeight: 500, fontStyle: 'italic',
                  border: '1px solid #2d4f7c',
                  whiteSpace: 'nowrap',
                  position: 'sticky', top: 0,
                  borderBottom: '2px solid #2d4f7c',
                }}>
                  {col.team || '—'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((batch, rowIdx) => {
              const displayBatchNo = batch.parent_blend_batch_no || batch.blend_batch_no;
              const stepData = batchStepMap[batch.id] || {};
              const rowBg = rowIdx % 2 === 0 ? '#fff' : '#f9fafb';
              return (
                <tr key={batch.id}>
                  {/* Batch label cell */}
                  <td style={{
                    background: rowBg, padding: '7px 12px',
                    border: '1px solid #e5e7eb',
                    position: 'sticky', left: 0, zIndex: 2,
                    borderRight: '2px solid #cbd5e1',
                    boxShadow: '2px 0 4px rgba(0,0,0,.06)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                        {displayBatchNo}
                      </span>
                      {batch.strength_code && (
                        <span style={{ fontSize: 7, fontWeight: 700, padding: '1px 4px', borderRadius: 2, background: '#dbeafe', color: '#1d4ed8', flexShrink: 0 }}>
                          {batch.strength_code}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 9, color: '#6b7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: BATCH_COL_W - 24 }}>
                      {batch.product_name}
                    </div>
                  </td>
                  {/* Step cells */}
                  {columns.map(col => {
                    const step = stepData[col.step_id];
                    const hours = cellHours(step);
                    const cs = cellStyle(step);
                    const tooltip = step
                      ? step.ticked_at
                        ? `${col.step_name}\nCompleted by: ${step.ticked_by || '—'}\nCompleted: ${formatDate(step.ticked_at)}${hours ? `\nTurnaround: ${hours}h` : ''}${step.is_auto_ticked ? '\n(Auto-ticked)' : ''}`
                        : step.is_waiting
                          ? `${col.step_name}\nAwaiting action from: ${col.team}\nWaiting since previous step completed${step.wait_days ? ` (${(step.wait_days * 24).toFixed(1)}h ago)` : ''}`
                          : `${col.step_name}\nNot yet actionable — waiting on dependencies`
                      : 'Not applicable for this batch';
                    return (
                      <Tooltip key={col.step_id} title={<span style={{ whiteSpace: 'pre-line', fontSize: 11 }}>{tooltip}</span>}>
                        <td style={{
                          background: step?.ticked_at ? cs.bg : (step?.is_waiting ? cs.bg : rowBg),
                          padding: '0',
                          border: '1px solid #e5e7eb',
                          textAlign: 'center',
                          minWidth: STEP_COL_W,
                          cursor: 'default',
                        }}>
                          {hours != null ? (
                            <span style={{
                              display: 'block', padding: '7px 4px',
                              fontSize: 11, fontWeight: 700,
                              color: cs.color,
                            }}>
                              {hours}h
                            </span>
                          ) : step?.ticked_at ? (
                            <span style={{ display: 'block', padding: '7px 4px', fontSize: 11, color: '#059669' }}>✓</span>
                          ) : (
                            <span style={{ display: 'block', padding: '7px 4px', fontSize: 11, color: step?.is_waiting ? '#b45309' : '#d1d5db' }}>
                              {step?.is_waiting ? '⏳' : step ? '○' : '—'}
                            </span>
                          )}
                        </td>
                      </Tooltip>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OsdDispatch;
